'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast, Toaster } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface Device {
  id: string;
  hostname: string;
  categoria: string;
  stato: string;
  reparto: string;
  piano: number;
  stanza: string;
  postazione: string;
  priorita: string;
  pos_x: number;
  pos_y: number;
  ip_address: string | null;
  reparto_id: string | null;
  sede_id: string | null;
}

interface Sede {
  id: string;
  nome: string;
}

interface Reparto {
  id: string;
  nome: string;
  piano: number;
  sede_id: string | null;
}

const PRIORITA_COLORS: Record<string, string> = {
  critico: 'bg-red-500',
  importante: 'bg-amber-500',
  standard: 'bg-sky-500',
  dismettibile: 'bg-gray-500',
  in_prova: 'bg-purple-500',
};

const STATO_COLORS: Record<string, string> = {
  online: 'bg-emerald-500',
  offline: 'bg-red-500',
  latenza_alta: 'bg-amber-500',
  intermittente: 'bg-orange-500',
  manutenzione: 'bg-blue-500',
};

export default function MappaPlanimetricaContent() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [sedi, setSedi] = useState<Sede[]>([]);
  const [reparti, setReparti] = useState<Reparto[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSede, setSelectedSede] = useState<string>('all');
  const [selectedPiano, setSelectedPiano] = useState<number>(0);
  const [filterStato, setFilterStato] = useState<string>('all');
  const [filterCategoria, setFilterCategoria] = useState<string>('all');
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const { canWrite } = useAuth();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, sRes, rRes] = await Promise.all([
        supabase.from('devices').select('id, hostname, categoria, stato, reparto, piano, stanza, postazione, priorita, pos_x, pos_y, ip_address, reparto_id, sede_id').order('hostname'),
        supabase.from('sedi').select('id, nome').eq('is_active', true).order('nome'),
        supabase.from('reparti').select('id, nome, piano, sede_id').eq('is_active', true).order('nome'),
      ]);
      if (dRes.error) throw dRes.error;
      setDevices((dRes.data || []) as Device[]);
      setSedi(sRes.data || []);
      setReparti(rRes.data || []);
    } catch (err: any) {
      toast.error('Errore caricamento: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const pianiDisponibili = Array.from(new Set(
    reparti.filter(r => selectedSede === 'all' || r.sede_id === selectedSede).map(r => r.piano)
  )).sort((a, b) => a - b);

  const filteredDevices = devices.filter(d => {
    if (selectedSede !== 'all' && d.sede_id !== selectedSede) return false;
    if (d.piano !== selectedPiano) return false;
    if (filterStato !== 'all' && d.stato !== filterStato) return false;
    if (filterCategoria !== 'all' && d.categoria !== filterCategoria) return false;
    return true;
  });

  const handleMouseDown = (e: React.MouseEvent, deviceId: string) => {
    if (!canWrite()) return;
    e.preventDefault();
    const rect = mapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const device = devices.find(d => d.id === deviceId);
    if (!device) return;
    const mapW = rect.width;
    const mapH = rect.height;
    const devX = (device.pos_x / 100) * mapW;
    const devY = (device.pos_y / 100) * mapH;
    setDragOffset({ x: e.clientX - rect.left - devX, y: e.clientY - rect.top - devY });
    setDraggingId(deviceId);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!draggingId || !mapRef.current) return;
    const rect = mapRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left - dragOffset.x) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top - dragOffset.y) / rect.height) * 100));
    setDevices(prev => prev.map(d => d.id === draggingId ? { ...d, pos_x: x, pos_y: y } : d));
  };

  const handleMouseUp = async () => {
    if (!draggingId) return;
    const device = devices.find(d => d.id === draggingId);
    if (device) {
      try {
        await supabase.from('devices').update({ pos_x: device.pos_x, pos_y: device.pos_y }).eq('id', draggingId);
        toast.success('Posizione salvata');
      } catch (err: any) {
        toast.error('Errore salvataggio posizione: ' + err.message);
      }
    }
    setDraggingId(null);
  };

  const pianoLabel = (p: number) => p === 0 ? 'Piano Terra' : p < 0 ? `Interrato ${Math.abs(p)}` : `Piano ${p}`;

  return (
    <div className="flex flex-col h-full bg-background">
      <Toaster richColors position="top-right" />
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-surface">
        <div>
          <h1 className="text-2xl font-bold text-white">Mappa Planimetrica</h1>
          <p className="text-sm text-muted-color mt-0.5">Posizione fisica dispositivi per piano — trascina per riposizionare</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-color">
          <Icon name="CursorArrowRaysIcon" size={16} />
          {canWrite() ? 'Trascina i dispositivi per riposizionarli' : 'Sola lettura'}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 px-6 py-3 border-b border-border bg-surface">
        <select value={selectedSede} onChange={e => setSelectedSede(e.target.value)}
          className="bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500">
          <option value="all">Tutte le sedi</option>
          {sedi.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
        </select>
        <div className="flex gap-1">
          {(pianiDisponibili.length > 0 ? pianiDisponibili : [0, 1, 2]).map(p => (
            <button key={p} onClick={() => setSelectedPiano(p)}
              className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${selectedPiano === p ? 'bg-sky-500 text-white' : 'bg-surface-elevated text-muted-color hover:text-white'}`}>
              {pianoLabel(p)}
            </button>
          ))}
        </div>
        <select value={filterStato} onChange={e => setFilterStato(e.target.value)}
          className="bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500">
          <option value="all">Tutti gli stati</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
          <option value="latenza_alta">Latenza Alta</option>
          <option value="manutenzione">Manutenzione</option>
        </select>
        <select value={filterCategoria} onChange={e => setFilterCategoria(e.target.value)}
          className="bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500">
          <option value="all">Tutte le categorie</option>
          <option value="laboratorio">Laboratorio</option>
          <option value="medico">Medico</option>
          <option value="server">Server</option>
          <option value="ufficio">Ufficio</option>
          <option value="rete">Rete</option>
        </select>
        <span className="ml-auto text-sm text-muted-color self-center">{filteredDevices.length} dispositivi su questo piano</span>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Map Canvas */}
        <div className="flex-1 p-4 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div
              ref={mapRef}
              className="relative w-full h-full bg-surface border-2 border-border rounded-xl overflow-hidden cursor-default select-none"
              style={{ minHeight: '500px' }}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {/* Grid background */}
              <div className="absolute inset-0 opacity-10"
                style={{ backgroundImage: 'linear-gradient(#4b5563 1px, transparent 1px), linear-gradient(90deg, #4b5563 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

              {/* Floor label */}
              <div className="absolute top-4 left-4 bg-surface/80 backdrop-blur-sm border border-border rounded-lg px-3 py-1.5">
                <span className="text-sm font-bold text-white">{pianoLabel(selectedPiano)}</span>
              </div>

              {/* Devices */}
              {filteredDevices.map(device => (
                <div
                  key={device.id}
                  className={`absolute group flex flex-col items-center cursor-${canWrite() ? 'grab active:cursor-grabbing' : 'pointer'} z-10`}
                  style={{ left: `${device.pos_x}%`, top: `${device.pos_y}%`, transform: 'translate(-50%, -50%)' }}
                  onMouseDown={e => handleMouseDown(e, device.id)}
                  onClick={() => setSelectedDevice(device)}
                >
                  <div className={`w-10 h-10 rounded-xl border-2 flex items-center justify-center shadow-lg transition-transform group-hover:scale-110 ${draggingId === device.id ? 'scale-110 shadow-sky-500/30' : ''} ${selectedDevice?.id === device.id ? 'border-sky-400 bg-sky-500/20' : 'border-border bg-surface'}`}>
                    <div className={`w-3 h-3 rounded-full ${STATO_COLORS[device.stato] || 'bg-gray-500'}`} />
                  </div>
                  <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border border-surface ${PRIORITA_COLORS[device.priorita] || 'bg-gray-500'}`} />
                  <span className="mt-1 text-xs font-semibold text-white bg-surface/90 px-1.5 py-0.5 rounded whitespace-nowrap max-w-24 truncate">
                    {device.hostname}
                  </span>
                </div>
              ))}

              {filteredDevices.length === 0 && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-color">
                  <Icon name="MapIcon" size={48} className="mb-3 opacity-30" />
                  <p className="text-lg font-semibold">Nessun dispositivo su questo piano</p>
                  <p className="text-sm mt-1">Assegna i dispositivi a questo piano dall&apos;inventario</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Side Panel */}
        <div className="w-72 border-l border-border bg-surface flex flex-col overflow-hidden">
          <div className="p-4 border-b border-border">
            <h3 className="font-bold text-white text-sm">Legenda</h3>
            <div className="mt-3 space-y-2">
              <p className="text-xs font-semibold text-muted-color uppercase tracking-wider">Stato</p>
              {[['online', 'Online'], ['offline', 'Offline'], ['latenza_alta', 'Latenza Alta'], ['manutenzione', 'Manutenzione']].map(([k, v]) => (
                <div key={k} className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${STATO_COLORS[k]}`} />
                  <span className="text-xs text-secondary-color">{v}</span>
                </div>
              ))}
              <p className="text-xs font-semibold text-muted-color uppercase tracking-wider mt-3">Priorità (angolo)</p>
              {[['critico', 'Critico'], ['importante', 'Importante'], ['standard', 'Standard'], ['dismettibile', 'Dismettibile'], ['in_prova', 'In Prova']].map(([k, v]) => (
                <div key={k} className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${PRIORITA_COLORS[k]}`} />
                  <span className="text-xs text-secondary-color">{v}</span>
                </div>
              ))}
            </div>
          </div>

          {selectedDevice ? (
            <div className="flex-1 overflow-y-auto p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-white text-sm">Dettaglio Dispositivo</h3>
                <button onClick={() => setSelectedDevice(null)} className="p-1 rounded hover:bg-surface-elevated text-muted-color">
                  <Icon name="XMarkIcon" size={16} />
                </button>
              </div>
              <div className="space-y-2 text-sm">
                <div className="bg-surface-elevated rounded-lg p-3">
                  <p className="font-bold text-white text-base">{selectedDevice.hostname}</p>
                  <p className="text-muted-color text-xs mt-0.5">{selectedDevice.categoria}</p>
                </div>
                {[
                  ['Stato', selectedDevice.stato],
                  ['Priorità', selectedDevice.priorita],
                  ['IP', selectedDevice.ip_address || '—'],
                  ['Reparto', selectedDevice.reparto || '—'],
                  ['Stanza', selectedDevice.stanza || '—'],
                  ['Postazione', selectedDevice.postazione || '—'],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between">
                    <span className="text-muted-color">{label}:</span>
                    <span className="text-white font-semibold">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-4">
              <h3 className="font-bold text-white text-sm mb-3">Dispositivi Piano</h3>
              <div className="space-y-2">
                {filteredDevices.map(d => (
                  <button key={d.id} onClick={() => setSelectedDevice(d)}
                    className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-surface-elevated transition-colors text-left">
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${STATO_COLORS[d.stato] || 'bg-gray-500'}`} />
                    <span className="text-sm text-white truncate flex-1">{d.hostname}</span>
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${PRIORITA_COLORS[d.priorita] || 'bg-gray-500'}`} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
